import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, formatBytes, formatRelativeTime } from '../lib/api';
import {
  HardDrive,
  Plus,
  Server,
  Clock,
  AlertTriangle,
  ChevronRight,
  Copy,
  Check,
} from 'lucide-react';
import { useState } from 'react';

interface Device {
  id: string;
  name: string;
  hostname: string | null;
  os_info: string | null;
  last_seen: string | null;
  snapshot_count: number;
  disk_usage: {
    used: number;
    total: number;
    percent: number;
  } | null;
  created_at: string;
}

interface DevicesResponse {
  success: boolean;
  data: Device[];
}

export default function Dashboard() {
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get<DevicesResponse>('/api/devices'),
  });

  const devices = data?.data || [];

  const copyInstallCommand = () => {
    navigator.clipboard.writeText('curl -fsSL https://watchthesize.com/install.sh | bash');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl bg-red-50 border border-red-200 p-6 text-center">
        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
        <p className="text-red-700">Failed to load devices. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Dashboard</h1>
          <p className="text-neutral-500 mt-1">Monitor disk usage across your devices</p>
        </div>
        <button
          onClick={() => setShowAddDevice(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-all hover:shadow-xl hover:shadow-primary-500/40"
        >
          <Plus className="h-4 w-4" />
          Add Device
        </button>
      </div>

      {/* Stats Overview */}
      {devices.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={Server}
            label="Total Devices"
            value={devices.length.toString()}
          />
          <StatCard
            icon={HardDrive}
            label="Total Storage"
            value={formatBytes(
              devices.reduce((sum, d) => sum + (d.disk_usage?.total || 0), 0)
            )}
          />
          <StatCard
            icon={HardDrive}
            label="Used Storage"
            value={formatBytes(
              devices.reduce((sum, d) => sum + (d.disk_usage?.used || 0), 0)
            )}
          />
          <StatCard
            icon={Clock}
            label="Active Devices"
            value={devices.filter((d) => {
              if (!d.last_seen) return false;
              const lastSeen = new Date(d.last_seen);
              const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
              return lastSeen > hourAgo;
            }).length.toString()}
          />
        </div>
      )}

      {/* Devices List */}
      {devices.length === 0 ? (
        <EmptyState onAddDevice={() => setShowAddDevice(true)} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {devices.map((device) => (
            <DeviceCard key={device.id} device={device} />
          ))}
        </div>
      )}

      {/* Add Device Modal */}
      {showAddDevice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowAddDevice(false)}
          />
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Add a New Device</h2>
            <p className="text-neutral-600 mb-6">
              Run this command on your Linux server to install the WatchTheSize client:
            </p>

            <div className="relative rounded-xl bg-neutral-900 p-4 font-mono text-sm text-green-400">
              <code>curl -fsSL https://watchthesize.com/install.sh | bash</code>
              <button
                onClick={copyInstallCommand}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg bg-neutral-800 p-2 text-neutral-400 transition-colors hover:bg-neutral-700 hover:text-white"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>

            <p className="mt-4 text-sm text-neutral-500">
              After installation, follow the link shown in the terminal to connect your device.
            </p>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAddDevice(false)}
                className="rounded-xl bg-neutral-100 px-5 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm border border-neutral-200/50">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-primary-50 p-2.5 text-primary-600">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-neutral-500">{label}</p>
          <p className="text-xl font-bold text-neutral-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

function DeviceCard({ device }: { device: Device }) {
  const diskPercent = device.disk_usage?.percent || 0;
  const isWarning = diskPercent > 80;
  const isCritical = diskPercent > 90;

  return (
    <Link
      to={`/devices/${device.id}`}
      className="group rounded-2xl bg-white p-5 shadow-sm border border-neutral-200/50 transition-all hover:shadow-md hover:border-primary-200"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`rounded-xl p-2.5 ${
              isCritical
                ? 'bg-red-50 text-red-600'
                : isWarning
                  ? 'bg-yellow-50 text-yellow-600'
                  : 'bg-primary-50 text-primary-600'
            }`}
          >
            <Server className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-neutral-900">{device.name}</h3>
            <p className="text-xs text-neutral-500">{device.hostname || 'Unknown host'}</p>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-neutral-300 transition-transform group-hover:translate-x-1 group-hover:text-primary-500" />
      </div>

      {device.disk_usage ? (
        <>
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-sm text-neutral-600">Disk Usage</span>
            <span
              className={`text-sm font-semibold ${
                isCritical
                  ? 'text-red-600'
                  : isWarning
                    ? 'text-yellow-600'
                    : 'text-neutral-900'
              }`}
            >
              {diskPercent}%
            </span>
          </div>
          <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                isCritical
                  ? 'bg-red-500'
                  : isWarning
                    ? 'bg-yellow-500'
                    : 'bg-primary-500'
              }`}
              style={{ width: `${Math.min(diskPercent, 100)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            {formatBytes(device.disk_usage.used)} / {formatBytes(device.disk_usage.total)}
          </p>
        </>
      ) : (
        <p className="text-sm text-neutral-400">No data yet</p>
      )}

      <div className="mt-4 flex items-center justify-between pt-4 border-t border-neutral-100">
        <span className="text-xs text-neutral-400">
          {device.last_seen ? `Last seen ${formatRelativeTime(device.last_seen)}` : 'Never synced'}
        </span>
        <span className="text-xs text-neutral-400">{device.snapshot_count} snapshots</span>
      </div>
    </Link>
  );
}

function EmptyState({ onAddDevice }: { onAddDevice: () => void }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-neutral-200 bg-white p-12 text-center">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50">
        <Server className="h-8 w-8 text-primary-600" />
      </div>
      <h3 className="text-lg font-semibold text-neutral-900 mb-2">No devices yet</h3>
      <p className="text-neutral-500 mb-6 max-w-sm mx-auto">
        Add your first Linux server to start monitoring disk usage.
      </p>
      <button
        onClick={onAddDevice}
        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-all hover:shadow-xl hover:shadow-primary-500/40"
      >
        <Plus className="h-4 w-4" />
        Add Your First Device
      </button>
    </div>
  );
}

